import { useMutation, useSuspenseQuery } from '@apollo/client';
import React from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
  useToast,
} from '@eluve/components';

import {
  getGlobalDefaultBundleQuery,
  insertGlobalDefaultBundleMutation,
  updateGlobalDefaultBundleMutation,
} from './template-bundle.operations';

export interface GlobalDefaultBundleOption {
  id: string;
  displayName: string;
}

interface GlobalDefaultBundleSelectProps {
  /** Active bundles the global default can point at. */
  options: GlobalDefaultBundleOption[];
}

/**
 * Picks the single system-wide global default bundle (the tenant_id IS NULL
 * row in template_bundle_defaults). This is the safety net resolved when a
 * bundles-enabled tenant has no enabled bundle of its own. There is always
 * exactly one global default and it cannot be cleared — selecting only
 * switches which bundle the global row points at.
 */
export const GlobalDefaultBundleSelect: React.FC<
  GlobalDefaultBundleSelectProps
> = ({ options }) => {
  const toast = useToast();

  const {
    data: { templateBundleDefaults },
  } = useSuspenseQuery(getGlobalDefaultBundleQuery, {
    context: eluveAdminHasuraContext,
  });

  const current = templateBundleDefaults[0] ?? null;

  const mutationOptions = {
    context: eluveAdminHasuraContext,
    refetchQueries: ['getGlobalDefaultBundle'],
    onError: (error: Error) => {
      toast.error(`Failed to update global default bundle: ${error.message}`);
    },
  };

  const [updateGlobalDefault] = useMutation(
    updateGlobalDefaultBundleMutation,
    mutationOptions,
  );
  const [insertGlobalDefault] = useMutation(
    insertGlobalDefaultBundleMutation,
    mutationOptions,
  );

  const handleChange = (templateBundleId: string) => {
    if (templateBundleId === current?.templateBundleId) return;

    // Exactly one global row exists, so this is a plain update-or-insert —
    // no clear-then-set dance and no partial-index race.
    if (current) {
      updateGlobalDefault({
        variables: { id: current.id, templateBundleId },
        optimisticResponse: {
          updateTemplateBundleDefaultsByPk: {
            __typename: 'TemplateBundleDefaults',
            id: current.id,
            templateBundleId,
          },
        },
      });
    } else {
      insertGlobalDefault({ variables: { templateBundleId } });
    }
  };

  return (
    <Stack gap={1} className="max-w-md">
      <Label htmlFor="global-default-bundle">Global default bundle</Label>
      <Select
        value={current?.templateBundleId ?? undefined}
        onValueChange={handleChange}
      >
        <SelectTrigger id="global-default-bundle" className="w-full">
          <SelectValue placeholder="Select a global default bundle" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.displayName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Text size="xs" color="tertiary">
        Used as a fallback for any organization that doesn't have its own bundle
        available.
      </Text>
    </Stack>
  );
};
